
import React, { useState } from 'react';
import { Play, Image as ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import SEO from '../components/SEO';

const Gallery = () => {
    const { language } = useLanguage();
    const t = translations[language].gallery;
    const [activeTab, setActiveTab] = useState('all');
    const [activeModel, setActiveModel] = useState('all');
    const [selectedMedia, setSelectedMedia] = useState(null);

    // Mock Data - Replace with actual assets later
    const galleryItems = [
        {
            id: 1,
            type: 'video',
            src: '/assets/gallery/videos/BJ30-vid.mp4',
            thumbnail: '/assets/images/research-development/rd-hero.jpg',
            title: 'BAIC Brand Movie',
            category: 'corporate',
            model: 'bj30Hybrid'
        },
        {
            id: 2,
            type: 'image',
            src: '/assets/images/models/bj30.png',
            title: 'BJ30 Exterior',
            category: 'models',
            model: 'bj30Hybrid'
        },
        {
            id: 3,
            type: 'image',
            src: '/assets/images/models/bj40-honor.png',
            title: 'BJ40 Honor',
            category: 'offRoad',
            model: 'bj40Honor'
        },
        {
            id: 5,
            type: 'image',
            src: '/assets/images/models/u5-plus.png',
            title: 'U5 Plus Interior',
            category: 'interior',
            model: 'u5Plus'
        },
        {
            id: 6,
            type: 'image',
            src: '/assets/car.jpg',
            title: 'Showroom Event',
            category: 'events',
            model: 'bj40Pro'
        }
    ];

    const filteredItems = galleryItems.filter(item => {
        const typeMatch = activeTab === 'all' || item.type === (activeTab === 'videos' ? 'video' : 'image');
        const modelMatch = activeModel === 'all' || item.model === activeModel;
        return typeMatch && modelMatch;
    });

    const models = [
        { id: 'all', label: t.filterModels.all },
        { id: 'bj30Hybrid', label: t.filterModels.bj30Hybrid },
        { id: 'bj40Honor', label: t.filterModels.bj40Honor },
        { id: 'bj40Pro', label: t.filterModels.bj40Pro },
        { id: 'u5Plus', label: t.filterModels.u5Plus },
    ];

    return (
        <div className="min-h-screen bg-white pt-[80px] pb-16">
            <SEO
                title="BAIC Media Contact Oman - Enquire today!!"
                description="Browse the BAIC gallery to see off-road cars with modern style."
                canonicalUrl="https://baicoman.com/gallery"
            />
            <h1 className="sr-only">BAIC Gallery Oman</h1>
            {/* Header */}


            <div className="max-w-7xl mx-auto py-20 px-4 sm:px-6 lg:px-8">
                {/* Tabs */}
                <div className="flex flex-col items-center gap-8 mb-12">
                    {/* Media Type Tabs */}
                    <div className="inline-flex bg-gray-100 p-1.5 rounded-full relative">
                        {['all', 'videos', 'images'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`relative px-8 py-3 rounded-full text-sm font-bold uppercase tracking-wide transition-all duration-300 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                {t.tabs[tab]}
                                {activeTab === tab && (
                                    <motion.div
                                        layoutId="activeTab"
                                        className="absolute inset-0 bg-red-600 rounded-full -z-10 shadow-md"
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Model Tabs */}
                    <div className="flex flex-wrap justify-center gap-3">
                        {models.map((model) => (
                            <button
                                key={model.id}
                                onClick={() => setActiveModel(model.id)}
                                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${activeModel === model.id
                                    ? 'bg-black text-white border-black'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                                    }`}
                            >
                                {model.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Grid */}
                <motion.div
                    layout
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                    <AnimatePresence mode='popLayout'>
                        {filteredItems.map((item) => (
                            <motion.div
                                layout
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.3 }}
                                key={item.id}
                                className="group relative rounded-2xl overflow-hidden shadow-lg bg-gray-100 aspect-video cursor-pointer"
                                onClick={() => setSelectedMedia(item)}
                            >
                                {item.type === 'video' ? (
                                    <video
                                        src={item.src}
                                        loop
                                        muted
                                        playsInline
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                ) : (
                                    <>
                                        <img
                                            src={item.src}
                                            alt={item.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />
                                    </>
                                )}

                                {item.type === 'video' && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                        <div className="w-14 h-14 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 transition-all duration-300 group-hover:bg-red-600 group-hover:border-red-600 group-hover:scale-110">
                                            <Play className="w-6 h-6 text-white ml-1" fill="currentColor" />
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            {/* Lightbox Modal */}
            <AnimatePresence>
                {selectedMedia && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-lg flex items-center justify-center p-4 lg:p-12"
                        onClick={() => setSelectedMedia(null)}
                    >
                        <button
                            className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors"
                            onClick={() => setSelectedMedia(null)}
                        >
                            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden shadow-2xl relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {selectedMedia.type === 'video' ? (
                                selectedMedia.src.startsWith('http') ? (
                                    <iframe
                                        src={`${selectedMedia.src}?autoplay=1`}
                                        title={selectedMedia.title}
                                        className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                ) : (
                                    <video
                                        src={selectedMedia.src}
                                        autoPlay
                                        controls
                                        className="w-full h-full object-contain"
                                    />
                                )
                            ) : (
                                <img
                                    src={selectedMedia.src}
                                    alt={selectedMedia.title}
                                    className="w-full h-full object-contain"
                                />
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Gallery;
